<template>
    <div class="action-plan-editor">
      <page-section title="Action Plan Details" v-loading="loading" element-loading-text="Generating content...">
        <!-- Header Section -->
        <h1 class="action-plan-title">{{ actionPlan.name }}</h1>
  
        <!-- Summary Information -->
        <el-card class="summary-card">
          <div class="summary-grid">
            <div class="summary-item">
              <label>Facility:</label>
              <template v-if="summaryEditMode">
                <el-select v-model=editedActionPlan.facility :placeholder="getFacilityName(actionPlan.facility) || 'Select Facility'">
                  <el-option
                    v-for="facility in global.facilities"
                    :key="facility.id"
                    :label="facility.displayText"
                    :value="facility.id"
                  />
                </el-select>
              </template>
              <template v-else>
                <span>{{ getFacilityName(actionPlan.facility) }}</span>
              </template>
            </div>

            <div class="summary-item">
              <label>Assigned To:</label>
              <template v-if="summaryEditMode">
                <el-input v-model="editedActionPlan.assignedTo" :placeholder="actionPlan.assignedTo"></el-input>
              </template>
              <template v-else>
                <span>{{ actionPlan.assignedTo || 'Unassigned' }}</span>
              </template>
            </div>

            <div class="summary-item">
              <label>Progress:</label>
              <template v-if="summaryEditMode">
                <el-select v-model="editedActionPlan.progress" :placeholder="actionPlan.progress || 'Select Progress'">
                  <el-option label="New" value="New" />
                  <el-option label="In Progress" value="In Progress" />
                  <el-option label="Reviewing" value="Reviewing" />
                  <el-option label="Completed" value="Completed" />
                </el-select>
              </template>
              <template v-else>
                <el-tag :type="getProgressType(actionPlan.progress)" size="small">
                  {{ actionPlan.progress }}
                </el-tag>
              </template>
            </div>

            <div class="summary-item">
              <label>Created Date:</label>
              <span>{{ formatDate(actionPlan.created) }}</span>
            </div>
            <div class="summary-item">
              <label>Created By:</label>
              <span>{{ actionPlan.createdBy }}</span>
            </div>
            <div class="summary-item">
              <label>Updated Date:</label>
              <span>{{ formatDate(actionPlan.updated) }}</span>
            </div>
            <div class="summary-item">
              <label>Last Updated By:</label>
              <span>{{ actionPlan.updatedBy }}</span>
            </div>
            <div class="summary-item">
              <label>Number of Deficiencies:</label>
              <span>{{ actionPlan.numDeficiencies }}</span>
            </div>
          </div>
          <div class="summary-actions">
            <template v-if="summaryEditMode">
              <el-button data-testid="save-summary" type="primary" size="small" @click="saveActionPlan">Save</el-button>
              <el-button data-testid="cancel-summary" size="small" @click="cancelEdit">Cancel</el-button>
            </template>
            <template v-else>
              <el-button data-testid="edit-summary" size="small" @click="summaryEditMode = true">Edit</el-button>
            </template>
          </div>
        </el-card>
  
        <!-- Details Table -->
        <el-card class="details-card">
          <div slot="header">
            <span>Deficiency Details</span>
            <el-button data-testid="add-deficiency" 
              type="primary" 
              size="small" 
              @click="handleAddDeficiency"
              style="float: right;"
            >
              Add Deficiency
            </el-button>
          </div>
          <el-table :data="deficiencies">
            <el-table-column prop="deficiencyType" label="Regulation Cited" width="150"></el-table-column>
            <el-table-column prop="description" label="Description of Deficiency" min-width="400"></el-table-column>
            <el-table-column prop="severityLevel" label="Level of Severity" width="120"></el-table-column>
            <el-table-column label="Actions" width="150">
              <template slot-scope="scope">
                <el-button data-testid="edit-deficiency" 
                  size="mini" 
                  type="primary" 
                  @click="handleEdit(scope.row)"
                >
                  Edit
                </el-button>
                <el-button data-testid="delete-deficiency" 
                  size="mini" 
                  type="danger" 
                  @click="handleDelete(scope.row)"
                >
                  Delete
                </el-button>
              </template>
            </el-table-column>
          </el-table>

          <!-- Edit Deficiency Dialog -->
          <el-dialog 
            :title="isEditing ? 'Edit Deficiency' : 'Add Deficiency'" 
            :visible.sync="editDialogVisible"
            width="50%"
          >
            <el-form :model="currentDeficiency" label-width="120px">
              <el-form-item label="Regulation Cited">
                <el-input v-model="currentDeficiency.deficiencyType"></el-input>
              </el-form-item>
              <el-form-item label="Description">
                <el-input 
                  type="textarea" 
                  v-model="currentDeficiency.description"
                  :rows="3"
                ></el-input>
              </el-form-item>
              <el-form-item label="Severity Level">
                <el-select v-model="currentDeficiency.severityLevel">
                  <el-option label="Low" value="Low"></el-option>
                  <el-option label="Medium" value="Medium"></el-option>
                  <el-option label="High" value="High"></el-option>
                </el-select>
              </el-form-item>
            </el-form>
            <span slot="footer" class="dialog-footer">
              <el-button data-testid="cancel-deficiency" @click="editDialogVisible = false">Cancel</el-button>
              <el-button data-testid="save-deficiency" type="primary" @click="saveDeficiency">Save</el-button>
            </span>
          </el-dialog>

          <!-- Delete Confirmation Dialog -->
          <el-dialog
            title="Confirm Delete"
            :visible.sync="deleteDialogVisible"
            width="30%"
          >
            <span>Are you sure you want to delete this deficiency?</span>
            <span slot="footer" class="dialog-footer">
              <el-button data-testid="cancel-delete" @click="deleteDialogVisible = false">Cancel</el-button>
              <el-button data-testid="confirm-delete" type="danger" @click="confirmDelete">Delete</el-button>
            </span>
          </el-dialog>
        </el-card>
  
        <!-- Rich Text Editor Section -->
        <el-card class="editor-card">
          <div slot="header" class="editor-header">
            <span>Action Plan</span>
            <div class="editor-actions">
              <span class="save-status" :class="{ 'saving': isSaving }">
                {{ saveStatus }}
              </span>
              <el-button-group>
                <el-button data-testid="save-content" size="mini" @click="saveContent">
                  <i class="el-icon-upload2"></i> Save
                </el-button>
                <el-button data-testid="download-content" size="mini" @click="downloadContent" :loading="downloading">
                  <i class="el-icon-download"></i> Download
                </el-button>
                <el-button data-testid="generate-content" size="mini" @click="generateAIContent" :loading="loading" :disabled="loading">
                  <i class="el-icon-magic-stick"></i> Generate
                </el-button>
              </el-button-group>
            </div>
          </div>
          <vue-editor
            ref="editor"
            v-model="editorContent"
            :editor-toolbar="customToolbar"
            :placeholder="'Enter Action Plan Details'"
            @text-change="onTextChange"
            @focus="onEditorFocus"
            @blur="onEditorBlur"
          />
        </el-card>
        
      </page-section>
    </div>
  </template>
  
  <script>
  import PageSection from "../Shared/PageSection";
  import moment from "moment";
  import { VueEditor } from "vue2-editor";
  import html2pdf from 'html2pdf.js';
  
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
  
  export default {
    name: "ActionPlanEditor",
    components: {
      "page-section": PageSection,
      "vue-editor": VueEditor
    },
    data() {
      return {
        summaryEditMode: false,
        editedActionPlan: {},
        loading: false,
        actionPlan: {},
        deficiencies: [],
        editorContent: "",
        customToolbar: [
          ["bold", "italic", "underline"],
          [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
          [{ 'font': [] }],
          [{ 'list': 'ordered'}, { 'list': 'bullet' }],
          [{ 'align': [] }],
          ['link', 'image'],
          ['clean']
        ],
        global : {
          facilities: []
        },
        downloading: false,
        editDialogVisible: false,
        deleteDialogVisible: false,
        isEditing: false,
        currentDeficiency: {
          deficiencyType: '',
          description: '',
          severityLevel: 'Low'
        },
        deficiencyToDelete: null,
        autoSaveTimeout: null,
        lastSavedContent: '',
        isSaving: false,
        saveStatus: '',
      };
    },
    created() {
        this.debouncedAutoSave = debounce(() => {
            this.autoSave();
        }, 3000),
        this.loadActionPlan();
        this.global.facilities = this.$configStore.data.sites;
    },
    methods: {
        getFacilityName(facId) {
            const facility = this.$configStore.data.sites.find(f => f.id === Number(facId));
            return facility ? facility.displayText : "";
        },
        saveActionPlan() {
          const payload = {};
          if (!this.editedActionPlan.facility) {
            payload.facility = this.actionPlan.facility;
          } else {
            payload.facility = this.editedActionPlan.facility;
          }
          if (!this.editedActionPlan.assignedTo) {
            payload.assignedTo = this.actionPlan.assignedTo;
          } else {
            payload.assignedTo = this.editedActionPlan.assignedTo;
          }

          if (!this.editedActionPlan.progress) {
            payload.progress = this.actionPlan.progress;
          } else {
            payload.progress = this.editedActionPlan.progress;
          }
          this.$http.patch(`${window.CONFIG.compliance_api}/${this.$route.params.id}`, payload).then(() => {
            this.summaryEditMode = false;
            this.loadActionPlan();
          }).catch(err => {
            console.error('Failed to save action plan', err);
          });
        },
        cancelEdit() {
          this.summaryEditMode = false;
          this.loadActionPlan();
        },
        async loadActionPlan() {
            this.loading = true;
            try {
                const url = `${window.CONFIG.compliance_api}/${this.$route.params.id}`;
                const response = await this.$http.get(url);
                this.actionPlan = response.data.report;
                this.editedActionPlan = {
                    facility: null,
                    assignedTo: null,
                    progress: null
                };
                this.deficiencies = response.data.deficiencies;
                
                if (!this.actionPlan.actionplantext) {
                    await this.generateAIContent();
                } else {
                    this.editorContent = this.actionPlan.actionplantext;
                }
            } catch (error) {
                this.$message.error("Failed to load action plan");
            } finally {
                this.loading = false;
            }
        },
        formatDate(date) {
            return moment(date).format("MM/DD/YYYY");
        },
        getProgressType(progress) {
            if (progress === "Complete") {
                return "success";
            } else if (progress === "In Progress") {
                return "warning";
            } else if (progress === "Reviewing") {
                return "info";
            } else if (progress === "New") {
                return "danger";
            } else {
              return "info";
            }
        },
        onTextChange() {
            this.saveStatus = '';
            this.debouncedAutoSave();
        },
        onEditorFocus() {
            // console.log('Editor focused');
        },
        onEditorBlur() {
           this.debouncedAutoSave();
        },
        async autoSave() {
            if (this.editorContent === this.lastSavedContent) {
                return;
            }

            this.isSaving = true;
            this.saveStatus = 'Saving...';

            try {
                // Use the PATCH endpoint to update the report
                await this.$http.patch(`${window.CONFIG.compliance_api}/${this.$route.params.id}`, {
                    actionplantext: this.editorContent
                });
                
                this.lastSavedContent = this.editorContent;
                this.saveStatus = 'All changes saved';
            } catch (error) {
                console.error('Error auto-saving content:', error);
                this.saveStatus = 'Error saving changes';
                this.$message.error('Failed to save changes');
            } finally {
                this.isSaving = false;
            }
        },
        async saveContent() {
            await this.autoSave();
            this.$message.success('Content saved successfully');
        },
        async downloadContent() {
            this.downloading = true;
            try {
                const element = document.createElement('div');
                element.innerHTML = this.editorContent;
                
                // Add CSS to control page breaks
                const style = document.createElement('style');
                style.textContent = `
                    @media print {
                        h1, h2, h3, h4, h5, h6 {
                            page-break-after: avoid;
                            page-break-inside: avoid;
                        }
                        table {
                            page-break-inside: avoid;
                        }
                        img {
                            page-break-inside: avoid;
                        }
                        p {
                            orphans: 3;
                            widows: 3;
                        }
                    }
                `;
                element.appendChild(style);
                
                // Add title and metadata
                const title = document.createElement('h1');
                title.textContent = this.actionPlan.name;
                title.style.textAlign = 'center';
                title.style.marginBottom = '20px';
                element.insertBefore(title, element.firstChild);

                // Add facility and date info
                const metadata = document.createElement('div');
                metadata.innerHTML = `
                  <p style="text-align: right; margin-bottom: 20px;">
                    Facility: ${this.getFacilityName(this.actionPlan.facility)}<br>
                    Date: ${moment().format('MMMM D, YYYY')}
                  </p>
                `;
                element.insertBefore(metadata, element.firstChild);

                const opt = {
                  margin: 1,
                  filename: `action-plan-${this.$route.params.id}.pdf`,
                  image: { type: 'jpeg', quality: 0.98 },
                  html2canvas: { 
                    scale: 2,
                    useCORS: true,
                    logging: false
                  },
                  jsPDF: { 
                    unit: 'in', 
                    format: 'letter', 
                    orientation: 'portrait' 
                  },
                  pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
                };

                // Generate and download PDF
                await html2pdf().set(opt).from(element).save();
                this.$message.success('PDF downloaded successfully');
            } catch (error) {
                console.error('Error generating PDF:', error);
                this.$message.error('Failed to generate PDF');
            } finally {
                this.downloading = false;
            }
        },
        async generateAIContent() {
            this.loading = true;
            try {
                const response = await this.$http.post(`${window.CONFIG.compliance_api}/generate/${this.$route.params.id}`);
                const report = response.data;
                
                // Format the content
                const currentDate = moment().format('MMMM D, YYYY');
                let formattedContent = "";
                if (report.summary) {
                    formattedContent += `<h3>Summary:</h3><p>${report.summary}</p>`;
                }
                formattedContent += `<h2><strong>Plan of Correction – ${report.name}</strong></h2>`;
                formattedContent += `<p>Facility: ${this.getFacilityName(report.facility)}<br>`;
                formattedContent += `Current Date: ${currentDate}</p>`;
                
                // Table of Cited Regulations
                formattedContent += '<h3>Table of Cited Regulations and Deficiencies:</h3>';
                formattedContent += '<ul>';
                this.deficiencies.forEach(def => {
                    formattedContent += `<li><strong>${def.deficiencyType}:</strong> ${def.description}</li>`;
                });
                formattedContent += '</ul>';
                
                // Plan of Correction
                formattedContent += '<h3>Plan of Correction for Each Deficiency:</h3>';
                this.deficiencies.forEach((def, index) => {
                    formattedContent += `<p><strong>${index + 1}) ${def.deficiencyType}:</strong><br>`;
                    formattedContent += `${def.correction}</p>`;
                });
                
                this.editorContent = formattedContent;
                this.$message.success('Content generated successfully');
            } catch (error) {
                console.error('Error generating content:', error);
                this.$message.error('Failed to generate content');
            } finally {
                this.loading = false;
            }
        },
        async handleAddDeficiency() {
            this.isEditing = false;
            this.currentDeficiency = {
                deficiencyType: '',
                description: '',
                severityLevel: 'Low'
            };
            this.editDialogVisible = true;
        },
        handleEdit(deficiency) {
            this.isEditing = true;
            this.currentDeficiency = { ...deficiency };
            this.editDialogVisible = true;
        },
        handleDelete(deficiency) {
            this.deficiencyToDelete = deficiency;
            this.deleteDialogVisible = true;
        },
        async saveDeficiency() {
          try {
              const reportId = this.$route.params.id;
              const url = this.isEditing 
                  ? `${window.CONFIG.compliance_api}/${reportId}/${this.currentDeficiency.id}`
                  : `${window.CONFIG.compliance_api}/deficiencies/${reportId}`;

              const method = this.isEditing ? 'patch' : 'post';

              const response = await this.$http({
                  method,
                  url,
                  body: {
                    id: this.currentDeficiency.id,
                    compliance_report_id: this.currentDeficiency.complianceReportId,
                    deficiency_type: this.currentDeficiency.deficiencyType,
                    description: this.currentDeficiency.description,
                    severity_level: this.currentDeficiency.severityLevel,
                    correction: this.currentDeficiency.correction
                  }
              });

              const updatedDeficiency = response.data;

              if (this.isEditing) {
                  const index = this.deficiencies.findIndex(d => d.id === updatedDeficiency.id);
                  if (index !== -1) {
                      this.deficiencies.splice(index, 1, updatedDeficiency);
                  }
              } else {
                  this.deficiencies.push(updatedDeficiency);
              }

              this.editDialogVisible = false;
          } catch (error) {
              console.error('Error saving deficiency:', error);
          }
        },
        async confirmDelete() {
            try {
                const reportId = this.$route.params.id;
                const url = `/api/compliance/${reportId}/${this.deficiencyToDelete.id}`;

                await this.$http.delete(url);

                const index = this.deficiencies.findIndex(
                    d => d.id === this.deficiencyToDelete.id
                );
                if (index !== -1) {
                    this.deficiencies.splice(index, 1);
                }

                this.deleteDialogVisible = false;
            } catch (error) {
                console.error('Error deleting deficiency:', error);
            }
        }
    },
    beforeDestroy() {
        // Ensure any pending saves are completed before component is destroyed
        if (this.autoSaveTimeout) {
            clearTimeout(this.autoSaveTimeout);
        }
        this.autoSave().then(() => {
            // console.log('Auto-save completed before component destruction');
        }).catch((error) => {
            console.error('Error during auto-save before destruction:', error);
        });
    }
  };
  </script>
  
  <style>
  .action-plan-editor {
    padding: 20px;
    position: relative;
  }
  
  .action-plan-title {
    margin-bottom: 20px;
  }
  
  .summary-card {
    margin-bottom: 20px;
  }
  
  .summary-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 20px;
  }
  
  .summary-item label {
    font-weight: bold;
    display: block;
    margin-bottom: 5px;
  }

  .summary-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 20px;
    gap: 8px;
  }
  
  .details-card {
    margin-bottom: 20px;
  }
  
  .editor-card {
    margin-bottom: 20px;
  }
  
  .editor-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  
  .editor-actions {
    margin-left: auto;
  }
  .ql-editor h1 {
    font-size: 2rem !important;
    font-weight: bold;
  }

  .ql-editor h2 {
    font-size: 1.7rem !important;
    font-weight: bold;
  }

  .ql-editor h3 {
    font-size: 1.4rem !important;
    font-weight: bold;
  }

  .ql-editor h4 {
    font-size: 1.2rem !important;
    font-weight: bold;
  }

  .ql-editor h5 {
    font-size: 1rem !important;
    font-weight: bold;
  }

  .ql-editor h6 {
    font-size: 0.85rem !important;
    font-weight: bold;
  }

  .ql-editor {
    min-height: 300px;
    font-size: 14px;
    line-height: 1.6;
    padding: 20px;
    background: white;
  }

  .ql-editor p {
    margin-bottom: 1em;
  }

  .ql-editor ul, .ql-editor ol {
    margin-bottom: 1em;
    padding-left: 1.5em;
  }

  .ql-editor table {
    border-collapse: collapse;
    width: 100%;
    margin-bottom: 1em;
  }

  .ql-editor table td, .ql-editor table th {
    border: 1px solid #ccc;
    padding: 8px;
  }

  .ql-editor img {
    max-width: 100%;
    height: auto;
  }

  .ql-toolbar.ql-snow {
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
  }

  .ql-container.ql-snow {
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
  }
  
  .action-plan-editor .el-loading-mask {
    background-color: rgba(255, 255, 255, 0.9);
  }

  .action-plan-editor .el-loading-text {
    margin-top: 10px;
    font-size: 16px;
    color: #409EFF;
  }

  .save-status {
    margin-right: 10px;
    font-size: 0.9em;
    color: #67C23A;
  }

  .save-status.saving {
    color: #E6A23C;
  }

  .editor-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }
  </style>